/**
 * URL-forced playground player identity (`?rundotPlayer=<playerId>`).
 *
 * The cross-tab coordinator (tabCoordinator.ts) assigns distinct synthetic
 * players only within ONE browser profile — its registry lives in
 * localStorage. Two independent browsers / automation contexts each start
 * with an empty registry, both claim slot 0, and both land on the single
 * base identity, so the room server rejects the second with
 * DUPLICATE_SESSION. Pinning the player in the URL needs no shared state,
 * survives reload/HMR, and is trivially scriptable by agents:
 *   browser2.goto(devUrl + '?rundotPlayer=player2')
 *
 * The value is exchanged for a synthetic `{baseUid}_sandbox_{playerId}` uid
 * via /v1/sandbox-auth/exchange, so it must satisfy that endpoint's
 * validation rule (enforced server-side in
 * server/cloud-run/src/controllers/sandboxAuth.controller.ts).
 */

export const FORCED_PLAYER_PARAM = 'rundotPlayer'

/** Must match the server-side rule in sandboxAuth.controller.ts. */
const PLAYER_ID_REGEX = /^[a-zA-Z0-9_]{1,32}$/

/**
 * Read the forced player id from a `location.search`-style string.
 * Returns null when the param is absent/empty (normal coordinator behavior)
 * or invalid (warns loudly — a silently ignored typo would re-collide on
 * the base identity and surface as a confusing DUPLICATE_SESSION).
 */
export function parseForcedPlayerId(search: string | undefined): string | null {
  if (!search) return null
  const raw = new URLSearchParams(search).get(FORCED_PLAYER_PARAM)
  if (raw === null) return null
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null
  if (!PLAYER_ID_REGEX.test(trimmed)) {
    console.warn(
      `[RUN:playground] Ignoring invalid ?${FORCED_PLAYER_PARAM}= value ` +
      `"${raw}" — must match ${PLAYER_ID_REGEX}. Falling back to automatic player assignment ` +
      '(first browser/tab = base identity; tabs in the same window get distinct players ' +
      'automatically). To pin a distinct player in a separate browser or context, use a ' +
      `valid value, e.g. ?${FORCED_PLAYER_PARAM}=player2.`,
    )
    return null
  }
  return trimmed
}

/**
 * Remove the forced-player param from the current URL without navigating.
 * Used by toolbar player switching so an explicit switch is not overridden
 * by the URL on the reload that applies it.
 */
export function stripForcedPlayerParamFromUrl(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has(FORCED_PLAYER_PARAM)) return
  url.searchParams.delete(FORCED_PLAYER_PARAM)
  window.history.replaceState(null, '', url.toString())
}
